/**
 * useAuthRole — hook que resuelve el rol del usuario actual contra /api/auth/me.
 *
 * - Cachea el role en localStorage (TTL 60s) para evitar llamadas repetidas en navegación.
 * - Cuando cambia el currentUser de Firebase (signin/signout), invalida cache y refetch.
 * - Devuelve {email, role, isLoading, error, refetch}.
 *
 * role posibles: 'user' | 'admin' | 'superadmin' | null
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';

const CACHE_KEY = 'tia_matcher_auth_role';
const CACHE_TTL_MS = 60 * 1000; // 60s

function readCache(email) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.email !== email) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeCache(email, role) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ email, role, ts: Date.now() }));
  } catch (_) {
    // ignore
  }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (_) {
    // ignore
  }
}

export function useAuthRole() {
  const { currentUser } = useAuth();
  const email = currentUser?.email || null;

  const [role, setRole] = useState(() => {
    const cached = email ? readCache(email) : null;
    return cached ? cached.role : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastEmailRef = useRef(null);

  const fetchRole = useCallback(async (force = false) => {
    if (!email) {
      setRole(null);
      return;
    }
    if (!force) {
      const cached = readCache(email);
      if (cached) {
        setRole(cached.role);
        return;
      }
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/me`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      const resolvedRole = data?.role || null;
      setRole(resolvedRole);
      writeCache(email, resolvedRole);
    } catch (err) {
      console.error('[useAuthRole] error:', err);
      setError(err.message || 'Error al obtener rol');
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  // Cuando cambia el email del usuario logueado, invalidar cache y refetch.
  useEffect(() => {
    if (lastEmailRef.current !== email) {
      lastEmailRef.current = email;
      clearCache();
      fetchRole(true);
    }
  }, [email, fetchRole]);

  const refetch = useCallback(() => {
    clearCache();
    return fetchRole(true);
  }, [fetchRole]);

  return { email, role, isLoading, error, refetch };
}
